import React, { useState } from "react";
import "./MessageTemplateModal.css";
import Input from "../../../../components/reusable-input/Input";
import Switch from "../../../../components/custom-switch/Switch";
import { Activity } from "../../../../apis/activityApis";
import { toast } from "react-toastify";

interface MessageTemplateModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  initialData?: any;
  metrics: Activity[];
  loading: boolean;
}

const conditionOptions = [
  { label: "Less Than", value: "Less Than" },
  { label: "Greater Than", value: "Greater Than" },
  { label: "Equal To", value: "Equal To" },
];

const MAX_MESSAGE_TEMPLATE_VALUE = 99999;

const getConditionLabel = (symbol: string) => {
  switch (symbol) {
    case ">":
      return "Greater Than";
    case "<":
      return "Less Than";
    case "=":
      return "Equal To";
    default:
      return symbol;
  }
};

const getConditionPhrase = (condition: string) => {
  switch (condition) {
    case "Greater Than":
    case ">":
      return "above";
    case "Less Than":
    case "<":
      return "below";
    case "Equal To":
    case "=":
      return "equal to";
    default:
      return "";
  }
};

const getPositiveWholeNumber = (inputValue: string) => {
  const numericValue = inputValue.replace(/\D/g, "");

  if (!numericValue) return "";

  return numericValue.slice(0, 5);
};

const getMetricName = (metrics: Activity[], metricId: string) => {
  return (
    metrics.find((activity) => String(activity.id) === String(metricId))
      ?.activityName || ""
  );
};

const get24HourTime = (timeValue: string) => {
  return timeValue?.slice(0, 5) || "00:00";
};

const MessageTemplateModal: React.FC<MessageTemplateModalProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  metrics,
  loading,
}) => {
  const [title, setTitle] = useState(initialData?.title || "");
  const [body, setBody] = useState(initialData?.desc || "");
  const [metric, setMetric] = useState(initialData?.metric || "");
  const [condition, setCondition] = useState(
    initialData?.condition ? getConditionLabel(initialData.condition) : "",
  );
  const [value, setValue] = useState(initialData?.value || "");
  const [time, setTime] = useState(get24HourTime(initialData?.time || ""));
  const [status, setStatus] = useState(initialData?.status ?? "Active");

  if (!open) return null;

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(getPositiveWholeNumber(e.target.value));
  };

  const handleSubmit = () => {
    if (!title || !body || !metric || !condition || !value || !time) {
      toast.error("Please fill all required fields");
      return;
    }

    if (Number(value) > MAX_MESSAGE_TEMPLATE_VALUE) {
      toast.error(`Value must be ${MAX_MESSAGE_TEMPLATE_VALUE} or less`);
      return;
    }

    onSubmit({
      title,
      body,
      metric,
      condition,
      value,
      time,
      status,
    });
  };

  const metricName = getMetricName(metrics, metric);
  const previewText =
    metricName && condition && value && time
      ? `Daily at ${time}, send SMS to users with ${metricName} ${getConditionPhrase(
          condition,
        )} ${value}.`
      : "";

  return (
    <div className="msgTemplateModal-overlay">
      <div className="msgTemplateModal-container">
        <h2 className="msgTemplateModal-heading">
          {initialData ? "Edit Template" : "Create Template"}
        </h2>

        <div className="msgTemplateModal-body">
          <div className="msgTemplateModal-section">
            <h3>Template Details</h3>

            <div className="msgTemplateModal-field">
              <label>
                Message Title <span className="required">*</span>{" "}
                <span className="questionModal-questionLength">
                  {title.length} / 50
                </span>
              </label>
              <Input
                type="text"
                value={title}
                maxLength={50}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter Title"
                search={false}
              />
            </div>

            <div className="msgTemplateModal-field">
              <label style={{ marginTop: "10px" }}>
                Message Body <span className="required">*</span>
              </label>
              <textarea
                className="msgTemplateModal-textarea"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Enter Message"
              />
            </div>
          </div>

          <div className="msgTemplateModal-section">
            <h3>Eligibility Criteria</h3>

            <div className="msgTemplateModal-row">
              <div className="msgTemplateModal-field">
                <label>
                  Metric <span className="required">*</span>
                </label>
                <select
                  value={metric}
                  onChange={(e) => setMetric(e.target.value)}
                  className="msgTemplateModal-select"
                >
                  <option value="">Select</option>
                  {metrics
                    .filter((activity) => activity.isMessageTemplateMetric)
                    .map((activity) => (
                      <option key={activity.id} value={activity.id}>
                        {activity.activityName}
                      </option>
                    ))}
                </select>
              </div>

              <div className="msgTemplateModal-field">
                <label>
                  Condition <span className="required">*</span>
                </label>
                <select
                  value={condition}
                  onChange={(e) => setCondition(e.target.value)}
                  className="msgTemplateModal-select"
                >
                  <option value="">Select</option>
                  {conditionOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="msgTemplateModal-field">
                <label>
                  Value <span className="required">*</span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={value}
                  onChange={handleValueChange}
                  placeholder="Enter Value"
                  className="msgTemplateModal-input"
                />
              </div>
            </div>
          </div>

          <div className="msgTemplateModal-section">
            <h3>Schedule</h3>
            <p className="msgTemplateModal-help">
              System checks eligible users daily at this time and sends
              SMS/Email reminders.
            </p>

            <div className="msgTemplateModal-field msgTemplateModal-timeField">
              <label>
                Time <span className="required">*</span>
              </label>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="msgTemplateModal-input msgTemplateModal-timeInput"
              />
            </div>
          </div>

          {previewText && (
            <div className="msgTemplateModal-section msgTemplateModal-preview">
              <h3>Preview</h3>
              <p>{previewText}</p>
            </div>
          )}

          {initialData && (
            <div className="msgTemplateModal-field">
              <Switch
                label={status}
                checked={status === "Active"}
                onChange={() =>
                  setStatus((prev: string) =>
                    prev === "Active" ? "Inactive" : "Active",
                  )
                }
              />
            </div>
          )}
        </div>

        <div className="msgTemplateModal-actions">
          <button className="cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="submit-btn"
            onClick={handleSubmit}
            disabled={
              !title || !body || !metric || !condition || !value || !time
            }
          >
            {loading ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MessageTemplateModal;
